// Copyright 2026, Imprivata, Inc.  All rights reserved.

import {
  type AppError,
  Authn,
  AuthnMethod,
  ErrorCode,
  type EventPayload,
  type FactorOption,
  type FeatureFlagsProp,
  type OrgPreferences,
} from '@imprivata-cloud/authn';
import {
  addSpanEvent,
  createSpanName,
  debug,
  error,
  ImprHeaders,
  instrument,
  SpanComponent,
  withAction,
} from '@imprivata-cloud/common';
import { jsonToBase64 } from '@imprivata-cloud/dataprivacy';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getOrgPreferences, setDefaultHeader } from '@/api/api';
import DefaultLogofrom from '@/assets/default_logo.svg?react';
import { AuthnLayout, LayoutHeaders } from '@/containers/Card/authn-layout';
import { InformationDialog } from '@/errorHandler/error-views/no-inline-enrollment';
import { UserNameForm } from '@/factors/username-form';
import { getWorkflowId } from '@/tracing';
import { resolveClientName } from '@/utils';

import { useBanner } from './hooks/useBanner';
import { loginHintOf, useEventHandler } from './hooks/useEventHandler';
import {
  headerOf,
  hexOf,
  INITIAL_VIEW,
  reduceView,
  showsInformationDialog,
  type ViewState,
} from './hooks/viewState';

const onSuccessSpan = createSpanName(SpanComponent.UI, 'service', 'on-success');
const onFailureSpan = createSpanName(SpanComponent.UI, 'service', 'on-failure');
const DEFAULT_FAILURE_REDIRECT_DELAY_MS = 100;
const DEVICE_INITIALIZATION_TIMEOUT_MS = 10_000;

const DEFAULT_LOGO = <DefaultLogofrom />;

const DEFAULT_DEVICE_FACTORS: AuthnMethod[] = [
  AuthnMethod.FACE_ID,
  AuthnMethod.PASSWORD,
  AuthnMethod.IMPR_ID,
  AuthnMethod.IMPR_PIN,
  AuthnMethod.TEMP_CODE,
  AuthnMethod.EMAIL_OTP,
];

// Everything AuthnFlow needs to render and drive one authentication ceremony.
// Each path computes its own launchConfig; AuthnFlow never reads query params
// or the EAM agent bridge itself.
export interface LaunchConfig {
  tenantId: string;
  applicationId?: string;
  authnWorkflowId?: string;
  clientName?: string;
  contextResource: { resourceType: string; contextType: string };
  oidcRequestData: { codeChallenge: string; codeChallengeMethod: string };
  initialFactors?: FactorOption[];
  hideFactorSwitch: boolean;
  fitVideoFrame?: boolean;
  loginHint: string | null;
  isOneToMany: boolean;
  disableAutoComplete: boolean;
}

interface RedirectTarget {
  redirectUrl: string;
  spanAttrs: NonNullable<Parameters<typeof addSpanEvent>[1]>;
}

// One reporter per path, closed over that path's own request/context id. It
// makes the backend call and returns where to send the user; AuthnFlow owns
// the redirect.
export interface OutcomeReporter {
  onSuccess(opts: { authorizationCode: string }): Promise<RedirectTarget>;
  onFailure(opts: {
    errorCode?: string;
    errorDescription?: string;
    loginHint?: string;
  }): Promise<RedirectTarget>;
  onMount?(): void;
  // True when the relying party is waiting on this sign-in, so a denied push
  // fails it back there instead of returning to the username form.
  failsOnPushDenied?: boolean;
}

const wait = (delayMs: number) =>
  new Promise(resolve => {
    window.setTimeout(resolve, delayMs);
  });

const onSuccessHandler = instrument(
  onSuccessSpan,
  withAction(
    'Redirection',
    async ({
      outcomeReporter,
      onFinished,
      ...opts
    }: {
      authorizationCode: string;
      outcomeReporter: OutcomeReporter;
      onFinished: (b: boolean) => void;
    }) => {
      try {
        debug('Processing authentication with code');
        const { redirectUrl, spanAttrs } =
          await outcomeReporter.onSuccess(opts);

        onFinished(true);
        addSpanEvent(createSpanName(onSuccessSpan, 'redirect'), spanAttrs);
        debug('Redirecting to relying party', { redirectUrl });
        window.location.href = redirectUrl;
      } catch (err) {
        error('Authentication failed', String(err));
        throw err; // Let the instrumentation handle the error
      }
    },
  ),
);

const onFailureHandler = instrument(
  onFailureSpan,
  withAction(
    'Redirection',
    async ({
      outcomeReporter,
      onFinished,
      redirectDelayMs = DEFAULT_FAILURE_REDIRECT_DELAY_MS,
      ...opts
    }: {
      redirectDelayMs?: number;
      errorCode?: string;
      errorDescription?: string;
      loginHint?: string;
      outcomeReporter: OutcomeReporter;
      onFinished: (b: boolean) => void;
    }) => {
      try {
        debug('Processing authentication failure');
        const { redirectUrl, spanAttrs } =
          await outcomeReporter.onFailure(opts);

        onFinished(true);
        addSpanEvent(createSpanName(onFailureSpan, 'redirect'), spanAttrs);
        debug('Redirecting to relying party', {
          redirectUrl,
          redirectDelayMs,
          errorCode: opts.errorCode,
        });
        if (redirectDelayMs > 0) {
          await wait(redirectDelayMs);
        }
        window.location.href = redirectUrl;
      } catch (err) {
        error('Authentication failure handling failed', String(err));
        throw err; // Let the instrumentation handle the error
      }
    },
  ),
);

export const AuthnFlow: React.FC<{
  launchConfig: LaunchConfig;
  showUI: boolean;
  outcomeReporter: OutcomeReporter;
}> = ({ launchConfig, showUI, outcomeReporter }) => {
  const { t } = useTranslation();

  // Track showUI transitions so the transient banner is cleared when the EAM
  // window is shown again after being hidden.
  const prevShowUIRef = useRef(showUI);

  const { displayBanner, clearBanner, bannerStatus } = useBanner();

  const {
    initialFactors,
    oidcRequestData,
    applicationId,
    authnWorkflowId,
    clientName,
    tenantId,
    hideFactorSwitch,
    contextResource,
    fitVideoFrame,
    loginHint,
    isOneToMany,
    disableAutoComplete,
  } = launchConfig;

  const [username, setUsername] = useState<string | undefined>(
    () => loginHint ?? '',
  );

  // The Authn sub-machine's presentation state. Header, hex glyph and layout
  // flags are pure projections of this single value (see viewState).
  const [view, setView] = useState<ViewState>(INITIAL_VIEW);
  const dispatchView = useCallback(
    (payload: EventPayload) => setView(v => reduceView(v, payload)),
    [],
  );

  const [finished, setFinished] = useState(false);
  const [completionFailed, setCompletionFailed] = useState(false);
  // Undefined until the fetch settles, so the username form and <Authn> mount
  // with the tenant's labels rather than swapping them in afterwards.
  const [orgPreferences, setOrgPreferences] = useState<OrgPreferences>();
  const [screenError, setScreenError] = useState<AppError | undefined>(
    undefined,
  );
  /**
   * Authn Props
   */
  const deviceFactors = useMemo(() => {
    if (!initialFactors) {
      return DEFAULT_DEVICE_FACTORS;
    }
    // We need PASSWORD factor when using "face" since we could need to ENROLL/CONSENT
    if (
      initialFactors.some(factor => factor.factorType === AuthnMethod.FACE_ID)
    ) {
      return [AuthnMethod.FACE_ID, AuthnMethod.PASSWORD];
    }
    return initialFactors.map(factor => factor.factorType);
  }, [initialFactors]);

  const requestConfig = useMemo(
    () => ({
      clientName: resolveClientName(clientName),
      username: isOneToMany ? undefined : username,
      oidcRequestData,
    }),
    [oidcRequestData, username, isOneToMany, clientName],
  );

  const tracing = useMemo(() => ({ workflowId: getWorkflowId() ?? '' }), []);

  // Both terminal exits (success redirect, failure redirect/close) complete the
  // flow exactly once. A single latch makes the underlying completion call
  // idempotent across StrictMode's double-invoked effects and any duplicate
  // event. A rejected completion is terminal too, so the latch stays set.
  const redirectInitiatedRef = useRef(false);

  const onSuccess = useCallback(
    (opts: { authorizationCode: string }) => {
      if (redirectInitiatedRef.current) {
        debug('Redirect already initiated; ignoring duplicate onSuccess');
        return;
      }
      redirectInitiatedRef.current = true;
      // The handler rethrows so instrument records the span error; catch here to
      // keep the memoized callback from leaking an unhandled rejection.
      return onSuccessHandler({
        outcomeReporter,
        onFinished: setFinished,
        ...opts,
      }).catch(err => {
        error('Authentication success redirect failed', String(err));
        setCompletionFailed(true);
      });
    },
    [outcomeReporter],
  );

  const onFailure = useCallback(
    (opts: {
      redirectDelayMs?: number;
      errorCode?: string;
      errorDescription?: string;
      loginHint?: string;
    }) => {
      if (redirectInitiatedRef.current) {
        debug('Redirect already initiated; ignoring duplicate onFailure');
        return;
      }
      redirectInitiatedRef.current = true;
      return onFailureHandler({
        outcomeReporter,
        onFinished: setFinished,
        ...opts,
      }).catch(err => {
        error('Authentication failure redirect failed', String(err));
        setCompletionFailed(true);
      });
    },
    [outcomeReporter],
  );

  // Terminal error screens (account locked/disabled, enrollment
  // unsupported) are dead ends for this session: acknowledging them fails the
  // flow back to the RP with the cause rather than dismissing locally.
  const handleTerminalScreenFailure = useCallback(
    (errorCode: string, errorDescription: string) =>
      onFailure({
        errorCode,
        errorDescription,
        loginHint: screenError ? loginHintOf(screenError) : undefined,
      }),
    [onFailure, screenError],
  );

  const errorScreens: Partial<Record<ErrorCode, React.ReactElement>> = useMemo(
    () => ({
      [ErrorCode.ACCOUNT_LOCKED]: (
        <InformationDialog
          text={t('errors.idp-locked')}
          title={t('errors.account-locked-title')}
          buttonProps={{
            onClick: () =>
              handleTerminalScreenFailure(
                ErrorCode.ACCOUNT_LOCKED,
                t('errors.idp-locked'),
              ),
            children: <>{t('commons.continue-button')}</>,
          }}
        />
      ),
      [ErrorCode.UNSUPPORTED_INLINE_ENROLLMENT]: (
        <InformationDialog
          title={t('errors.face-not-enrolled-title')}
          text={t('errors.face-not-enrolled-text')}
          buttonProps={{
            onClick: () =>
              handleTerminalScreenFailure(
                ErrorCode.UNSUPPORTED_INLINE_ENROLLMENT,
                t('errors.face-not-enrolled-text'),
              ),
            children: <>{t('commons.continue-button')}</>,
          }}
        />
      ),
      [ErrorCode.ACCOUNT_DISABLED]: (
        <InformationDialog
          text={t('errors.account-disabled')}
          title={t('errors.account-locked-title')}
          buttonProps={{
            onClick: () =>
              handleTerminalScreenFailure(
                ErrorCode.ACCOUNT_DISABLED,
                t('errors.account-disabled'),
              ),
            children: <>{t('commons.continue-button')}</>,
          }}
        />
      ),
      [ErrorCode.UNSUPPORTED_OPERATION]: (
        <InformationDialog
          text={t('errors.something-unexpected')}
          buttonProps={{
            onClick: () =>
              handleTerminalScreenFailure(
                ErrorCode.UNSUPPORTED_OPERATION,
                t('errors.something-unexpected'),
              ),
            children: <>{t('commons.continue-button')}</>,
          }}
        />
      ),
      [ErrorCode.CREDENTIAL_EXPIRED]: (
        <InformationDialog
          title={t('errors.credential-expired')}
          text={t('errors.credential-expired-sub-message')}
          buttonProps={{
            onClick: () =>
              handleTerminalScreenFailure(
                ErrorCode.CREDENTIAL_EXPIRED,
                t('errors.credential-expired'),
              ),
            children: <>{t('commons.continue-button')}</>,
          }}
        />
      ),
    }),
    [t, handleTerminalScreenFailure],
  );

  const onCancel = useCallback(() => {
    debug('Authentication canceled by user');
    onFailure({ redirectDelayMs: 0, errorCode: 'user_cancelled' });
  }, [onFailure]);

  const canChangeUsername = !loginHint;

  const handleEvent = useEventHandler({
    isOneToMany,
    allowChangeUsername: canChangeUsername,
    failsOnPushDenied: outcomeReporter.failsOnPushDenied ?? false,
    onSuccess,
    onFailure,
    onCancel,
    setUsername,
    displayBanner,
    clearBanner,
    setScreenError,
    dispatchView,
  });

  /**
   * EAM specific `showUI` handling effect
   */
  useEffect(() => {
    debug('showUI', showUI);
    if (showUI && !prevShowUIRef.current) {
      debug('Window re-shown after hide, clearing banner');
      clearBanner();
    }
    prevShowUIRef.current = showUI;
  }, [showUI, clearBanner]);

  /**
   * Initial setup effect - setting headers, fetching org logo, etc.
   *
   * TODO: Identify what can be stripped out, removed
   */
  useEffect(() => {
    setDefaultHeader(
      ImprHeaders.ImprClientName,
      jsonToBase64({ name: resolveClientName(clientName) }),
    );
    setDefaultHeader(ImprHeaders.ImprTenantId, tenantId);
    outcomeReporter.onMount?.();
  }, [tenantId, clientName, outcomeReporter]);

  useEffect(() => {
    if (!tenantId) {
      setOrgPreferences({});
      return;
    }
    let current = true;
    void (async () => {
      try {
        const preferences = await getOrgPreferences();
        if (current) setOrgPreferences(preferences);
      } catch (e) {
        debug('Failed to fetch org preferences, using defaults', { error: e });
        if (current) setOrgPreferences({});
      }
    })();
    return () => {
      current = false;
    };
  }, [tenantId]);

  // Scroll the page up when virtual keyboard is hidden (input loses focus)
  useEffect(() => {
    // Fixes an issue on mobile when, after virtual keyboard is hidden, the page
    // remains scrolled down a bit and there is no way to scroll it up because
    // the container has "overflow: hidden" and there is no scroll bar
    const scrollHandler = () => {
      window.scrollTo(0, 0);
    };
    // 'focusout' is like 'blur' but bubbles, so it can be caught on 'window'
    window.addEventListener('focusout', scrollHandler);
    return () => {
      window.removeEventListener('focusout', scrollHandler);
    };
  }, []);

  const header = headerOf(view) ?? LayoutHeaders.AUTHENTICATION;
  const hexImage = hexOf(view);
  const isInformationDialog = showsInformationDialog(view);

  const featureFlags: FeatureFlagsProp = useMemo(() => {
    // TODO: determine based on tenantId once we decide what to do with IAD
    const flags: FeatureFlagsProp = { enableIAD: false };
    if (fitVideoFrame) {
      flags.fitVideoFrame = fitVideoFrame;
    }
    flags.deviceInitTimeoutMs = DEVICE_INITIALIZATION_TIMEOUT_MS;
    return flags;
  }, [fitVideoFrame]);

  if (finished || !showUI) {
    return null;
  }

  if (completionFailed) {
    return <InformationDialog text={t('errors.something-unexpected')} />;
  }

  if (screenError) {
    return errorScreens[screenError.code as ErrorCode] ?? null;
  }

  return (
    <AuthnLayout
      hexImage={hexImage}
      header={header}
      logoUrl={orgPreferences?.logoUrl || DEFAULT_LOGO}
      hideLayout={isInformationDialog}
      bannerStatus={bannerStatus}
    >
      {orgPreferences && !username && !isOneToMany && (
        <UserNameForm
          onSubmit={setUsername}
          initValue={username}
          usernameLabel={orgPreferences.usernameLabel}
          autoComplete={disableAutoComplete ? 'off' : undefined}
        />
      )}
      {orgPreferences && (username || isOneToMany) && (
        <Authn
          contextResource={contextResource}
          tenantId={tenantId}
          applicationId={applicationId}
          authnWorkflowId={authnWorkflowId}
          onEvent={handleEvent}
          deviceFactors={deviceFactors}
          factors={initialFactors}
          requestConfig={requestConfig}
          tracing={tracing}
          hideFactorSwitch={hideFactorSwitch}
          featureFlags={featureFlags}
          allowChangeUsername={canChangeUsername}
          orgPreferences={orgPreferences}
          autoComplete={disableAutoComplete ? 'off' : undefined}
        />
      )}
    </AuthnLayout>
  );
};
